import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Pill } from "lucide-react";
import { getMedicationColor } from "@/lib/medicationColors";

/**
 * MobileMedication — mirror of the Treatment Schedule Details panel from
 * the desktop Calendar page, optimised for mobile. Groups Drug Administration
 * events by drug name and shows the day pattern + actual dates.
 */
export default function MobileMedication() {
  const [appts, setAppts] = useState(null);

  useEffect(() => { api.get("/patient/appointments").then((r) => setAppts(r.data)); }, []);

  const groups = useMemo(() => {
    if (!appts) return [];
    const all = [...appts.future, ...appts.past].filter((e) => e.event_type === "Drug Administration");
    // Use the earliest event date as the "treatment start" so we can derive
    // absolute treatment-day numbers (Day 1, 22, 43, …) just like the desktop
    // Treatment Schedule Details panel.
    const startMs = all.length
      ? Math.min(...all.map((e) => new Date(e.date + "T00:00:00").getTime()))
      : null;
    const dayOf = (iso) => {
      if (!startMs) return null;
      const t = new Date(iso + "T00:00:00").getTime();
      return Math.round((t - startMs) / (1000 * 60 * 60 * 24)) + 1;
    };
    const map = new Map();
    all.forEach((e) => {
      const name = e.print_name || e.drug_name || e.name || "Unknown";
      if (!map.has(name)) {
        map.set(name, {
          name,
          color: getMedicationColor(name),
          dose: e.dose,
          route: e.route,
          location: e.location || e.provider?.department,
          dates: [],
        });
      }
      map.get(name).dates.push({
        date: e.date,
        day: dayOf(e.date),
        is_future: e.is_future,
        confirmation: e.confirmation,
      });
    });
    return [...map.values()]
      .map((g) => ({ ...g, dates: g.dates.sort((a, b) => a.date.localeCompare(b.date)) }));
  }, [appts]);

  if (!appts) return <div className="max-w-md mx-auto p-6 text-stone-500">Loading…</div>;

  return (
    <div className="max-w-md mx-auto pb-4" data-testid="mobile-medication">
      <header className="px-5 pt-6 pb-3">
        <h1 className="font-display text-2xl font-bold text-stone-900">Medication</h1>
        <p className="text-stone-500 text-sm">Your treatment schedule details.</p>
      </header>

      <section className="px-5 space-y-3">
        {groups.length === 0 && (
          <div className="bg-white border border-stone-200 rounded-2xl p-6 text-center text-stone-500 text-sm">
            No medication scheduled yet.
          </div>
        )}
        {groups.map((g) => (
          <div key={g.name} className="bg-white border border-stone-200 rounded-2xl p-4 relative overflow-hidden" data-testid={`med-group-${g.name.replace(/\W+/g, "-")}`}>
            <span className="absolute left-0 top-3 bottom-3 w-1 rounded-full" style={{ background: g.color.bg }} />
            <div className="pl-3">
              <div className="flex items-center gap-2 mb-1">
                <Pill className="w-4 h-4" style={{ color: g.color.bg }} />
                <span className="px-1.5 py-0.5 rounded text-[11px] font-bold" style={{ backgroundColor: g.color.bg, color: g.color.text }}>{g.name}</span>
                {g.dose && <span className="text-xs text-stone-600">{g.dose}</span>}
              </div>
              {(g.route || g.location) && (
                <div className="text-xs text-stone-500 mb-2">
                  {g.route}{g.location ? ` · ${g.location}` : ""}
                </div>
              )}
              <div className="cycle-days-row text-xs">
                <span className="cycle-days-label">Days: </span>
                {g.dates.map((d) => d.day).filter((x) => x != null).join(", ") || "—"}
                {g.dates.length > 0 && (
                  <>
                    {" "}<span className="cycle-days-paren">(</span>
                    <span className="cycle-days-dates">{g.dates.map((d) => new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })).join(", ")}</span>
                    <span className="cycle-days-paren">)</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
